import {
  FormControlSizes,
  MixpanelEvent,
  MixpanelEventProperty,
  MobileDataTable,
  Toggle,
  WSBlotterTableMaxRecordsReachedWarning,
  useAccordionFilterBuilder,
  useMixpanel,
  useWsBlotterTable,
  type Trade,
} from '@talos/kyoko';
import { useCallback } from 'react';
import { useFilteredTradesBlotter } from '../../../../Blotters/Trades/useFilteredTradesBlotter';
import type { BlotterTableTradeFilter } from '../../../../Blotters/Trades/useTradeFilter';
import { MobileBlotterToolbar } from '../MobileBlotterToolbar';
import { MobileCompactCardsToggle } from '../MobileCompactCardsToggle';
import { MobileShowAllDecimalsToggle } from '../MobileShowAllDecimalsToggle';
import { MobileTradeCard, useMobileTradeCardLayout } from './MobileTradeCard';
import { MobileTradeDetailFooter } from './MobileTradeDetailFooter';

interface MobileFilteredTradesProps {
  blotterID: string;
  tabLabel?: string;
  defaultFilter: BlotterTableTradeFilter;
  defaultColumns?: Parameters<typeof useFilteredTradesBlotter>[0]['defaultColumns'];
}

/**
 * Mobile counterpart to desktop `FilteredTrades`. Reuses the same WebSocket
 * subscription, column definitions, and filter logic — only the rendering
 * layer changes (`MobileDataTable` + `MobileTradeCard` instead of `BlotterTable`).
 *
 * The mobile header exposes filters through a modal sheet instead of the
 * desktop accordion. Summary line, export UI, and context/menu actions are
 * still skipped.
 *
 * Row taps open a {@link MobileRowDetailSheet} via the default
 * {@link MobileDataTable} behaviour — no explicit `onRowPress` needed.
 */
export function MobileFilteredTrades({
  blotterID,
  tabLabel,
  defaultFilter,
  defaultColumns,
}: MobileFilteredTradesProps) {
  const mixpanel = useMixpanel();
  const { density, rowID, columns, persistedBlotterTable, filteredTrades, request, startingRowLimit } =
    useFilteredTradesBlotter({
      blotterPersistID: blotterID,
      defaultFilter,
      defaultColumns,
      filterByOrderID: false,
      additionalFilterState: undefined,
      blotterTagPrefix: 'MOBILE_FILTERED_TRADES',
    });

  const blotterTable = useWsBlotterTable({
    request,
    persistence: persistedBlotterTable,
    rowID,
    density,
    clientLocalFilter: filteredTrades.clientSideFilter,
    columns,
    startingRowLimit,
    exportOptions: { name: 'Trades', tabLabel },
  });

  const filterBuilderAccordion = useAccordionFilterBuilder({
    filterBuilderProps: filteredTrades.filterBuilderProps,
  });

  const { changeFilter } = filteredTrades;

  const handleApiOrdersChange = useCallback(
    (value: boolean) => {
      mixpanel.track(MixpanelEvent.ApiOrdersToggle, { [MixpanelEventProperty.Enabled]: value });
      changeFilter(curr => ({ ...curr, HideApiCalls: !value }));
    },
    [changeFilter, mixpanel]
  );

  const handleOffPlatformTradesChange = useCallback(
    (value: boolean) => {
      mixpanel.track(MixpanelEvent.OffPlatformTradesToggle, { [MixpanelEventProperty.Enabled]: value });
      changeFilter(curr => ({ ...curr, HideOffPlatform: !value }));
    },
    [changeFilter, mixpanel]
  );

  const rowDetailFooter = useCallback((data: Trade) => <MobileTradeDetailFooter trade={data} />, []);
  const { cardHeight, cardRendererProps } = useMobileTradeCardLayout();

  return (
    <>
      <MobileBlotterToolbar
        {...filterBuilderAccordion}
        {...filteredTrades.blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        settingsContent={
          <>
            <MobileCompactCardsToggle type="Trade" />
            <Toggle
              label="API Orders"
              size={FormControlSizes.Default}
              tooltip="Include trades from orders submitted through the API in the results."
              checked={!filteredTrades.filter.HideApiCalls}
              onChange={handleApiOrdersChange}
              fullWidth
            />
            <Toggle
              label="Off Platform Trades"
              size={FormControlSizes.Default}
              tooltip="Display Off Platform Trades in the Trade Blotter"
              checked={!filteredTrades.filter.HideOffPlatform}
              onChange={handleOffPlatformTradesChange}
              fullWidth
            />
            <MobileShowAllDecimalsToggle />
          </>
        }
      />
      <WSBlotterTableMaxRecordsReachedWarning
        {...blotterTable.paginationLimit}
        getTimestamp={trade => trade.TransactTime}
      />
      <MobileDataTable<Trade>
        {...blotterTable}
        cardRenderer={MobileTradeCard}
        cardRendererProps={cardRendererProps}
        cardHeight={cardHeight}
        detailsTitle="Trade Details"
        rowDetailFooter={rowDetailFooter}
      />
    </>
  );
}
